import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { emApp, ehCelular } from '@/lib/app';
import { useModoEquipe } from '@/components/feature/ModoEquipe';
import { useVezDeFolha } from '@/lib/filaFolhas';
import {
  buscarMeuWhatsapp,
  definirMeuWhatsapp,
  salvarTelefoneNoPerfil,
  rotaBloqueiaWhatsapp,
  formatarWhatsapp,
  digitosWhats,
} from '@/lib/whatsapp';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Sheet from '@/components/ui/Sheet';
import PrimaryButton from '@/components/ui/PrimaryButton';
import Campo from '@/components/ui/Campo';

// ---------------------------------------------------------------------------
// "Quer receber pelo WhatsApp?" — folha que sobe a CADA abertura e volta ao app
// para quem está logado, enquanto a pessoa ainda não respondeu (precisa_perguntar).
// Se já respondeu (sim ou não), nunca mais aparece.
//
// Fica na fila junto com "instalar o app" (1º) e "avisos no celular" (2º):
// só abre quando chega a sua vez (3º) e depois que a anterior foi fechada.
//
// Não aparece em pagamento/Pix, checkout, ficha de inscrição, login, caixa,
// portaria, barraca, modo equipe nem na tela "O evento terminou".
// ---------------------------------------------------------------------------
export default function ConviteWhatsapp() {
  const { ativo: modoEquipe } = useModoEquipe();
  const { pathname } = useLocation();

  const [logado, setLogado] = useState(false);
  const [telaEstreita, setTelaEstreita] = useState(false);
  const [precisa, setPrecisa] = useState(false);
  const [telefone, setTelefone] = useState<string | null>(null);
  const [dispensado, setDispensado] = useState(false);

  const [mostrarCampo, setMostrarCampo] = useState(false);
  const [numero, setNumero] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const aquiNoCelular = emApp() || ehCelular() || telaEstreita;
  const rotaBloqueada = rotaBloqueiaWhatsapp(pathname);

  // Observa a sessão (some/aparece quando entra ou sai).
  useEffect(() => {
    let vivo = true;
    supabase.auth.getSession().then(({ data }) => {
      if (vivo) setLogado(Boolean(data.session?.user));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evento, session) => {
      setLogado(Boolean(session?.user));
    });
    return () => {
      vivo = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Tela estreita (celular / prévia em modo celular).
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const aplicar = () => setTelaEstreita(mq.matches);
    aplicar();
    mq.addEventListener('change', aplicar);
    return () => mq.removeEventListener('change', aplicar);
  }, []);

  const consultar = useCallback(async () => {
    if (!logado || modoEquipe || !aquiNoCelular) {
      setPrecisa(false);
      return;
    }
    try {
      const d = await buscarMeuWhatsapp();
      setTelefone(d.telefone);
      setPrecisa(d.logado && d.precisa_perguntar);
      if (!d.precisa_perguntar) {
        setMostrarCampo(false);
        setErro(null);
      }
    } catch {
      /* silencioso: tenta de novo na próxima abertura */
    }
  }, [logado, modoEquipe, aquiNoCelular]);

  // A cada abertura e volta ao app, pergunta de novo se ainda não respondeu.
  useEffect(() => {
    void consultar();
  }, [consultar]);

  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') void consultar();
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, [consultar]);

  const quer = precisa && !dispensado && !rotaBloqueada && !modoEquipe;
  const minhaVez = useVezDeFolha('convite-whatsapp', 4, quer);

  const temTelefone = digitosWhats(telefone).length >= 10;
  // Precisa digitar o número quando não há telefone no perfil ou quando a pessoa
  // tocou em "Trocar".
  const precisaDigitar = !temTelefone || mostrarCampo;
  const numeroCompleto = digitosWhats(numero).length >= 10;
  const podeConfirmar = precisaDigitar ? numeroCompleto : true;

  function trocar() {
    setNumero(temTelefone ? formatarWhatsapp(telefone) : '');
    setErro(null);
    setMostrarCampo(true);
  }

  async function confirmar() {
    if (precisaDigitar && !numeroCompleto) {
      setErro('Digite seu WhatsApp com DDD.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const informado = precisaDigitar ? digitosWhats(numero) : '';
      const r = await definirMeuWhatsapp(true, informado || null);
      if (informado) {
        await salvarTelefoneNoPerfil(informado);
        setTelefone(informado);
      } else if (r.telefone) {
        setTelefone(r.telefone);
      }
      setPrecisa(false);
      mostrarToast('Pronto! Você vai receber pelo WhatsApp.', 5000, 'sucesso');
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos salvar agora.'));
    } finally {
      setSalvando(false);
    }
  }

  function agoraNao() {
    setDispensado(true);
    setErro(null);
  }

  async function naoQuero() {
    setSalvando(true);
    setErro(null);
    try {
      await definirMeuWhatsapp(false);
      setPrecisa(false);
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos salvar agora.'));
    } finally {
      setSalvando(false);
    }
  }

  if (!quer || !minhaVez) return null;

  return (
    <Sheet aberto aoFechar={agoraNao}>
      <div className="flex flex-col items-center pt-2 text-center">
        <span className="flex h-16 w-16 items-center justify-center text-[#25D366]">
          <i className="ri-whatsapp-line text-[44px]" aria-hidden />
        </span>
        <h2 className="mt-3 text-[20px] font-extrabold leading-tight text-foreground-950">
          Quer receber pelo WhatsApp?
        </h2>
        <p className="mt-2 max-w-sm text-[15px] leading-relaxed text-foreground-600">
          Avisos dos seus eventos e novidades perto de você. Para parar, é só desligar no seu
          perfil.
        </p>
      </div>

      <div className="mt-5">
        {precisaDigitar ? (
          <Campo
            rotulo="Seu WhatsApp com DDD"
            value={numero}
            onChange={(v) => {
              setNumero(v);
              setErro(null);
            }}
            mascara="telefone"
            placeholder="(00) 00000-0000"
            inputMode="tel"
            autoComplete="tel"
          />
        ) : (
          <div className="flex items-center justify-between gap-3 rounded-[16px] bg-background-100 px-4 py-3">
            <span className="min-w-0 truncate text-[15px] font-semibold text-foreground-950">
              No número {formatarWhatsapp(telefone)}
            </span>
            <button
              type="button"
              onClick={trocar}
              className="aperta shrink-0 cursor-pointer whitespace-nowrap text-[15px] font-semibold text-primary-700"
            >
              Trocar
            </button>
          </div>
        )}

        {erro && (
          <p className="mt-2 flex items-start gap-1.5 text-[13px] text-danger-600" role="alert">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
              <i className="ri-error-warning-line" aria-hidden />
            </span>
            <span className="min-w-0 text-left">{erro}</span>
          </p>
        )}
      </div>

      <PrimaryButton
        cheio
        className="mt-5"
        icone="ri-whatsapp-line"
        carregando={salvando}
        disabled={!podeConfirmar}
        onClick={confirmar}
      >
        Sim, quero receber
      </PrimaryButton>

      <button
        type="button"
        onClick={agoraNao}
        className="aperta mx-auto mt-2 block cursor-pointer whitespace-nowrap py-2.5 text-[15px] font-semibold text-foreground-500"
      >
        Agora não
      </button>

      <button
        type="button"
        onClick={naoQuero}
        disabled={salvando}
        className="aperta mx-auto mt-0.5 block cursor-pointer whitespace-nowrap py-2 text-[13px] font-medium text-foreground-400 transition-colors hover:text-foreground-600 disabled:opacity-60"
      >
        Não quero receber pelo WhatsApp
      </button>
    </Sheet>
  );
}